import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Clock, ShieldCheck, BellRing } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";

const NOTIFY_OPTIONS = [
  { value: "all", label: "Every update", hint: "Receipt confirmed, then the final outcome" },
  { value: "final", label: "Only the final outcome", hint: "Admitted or Not admitted only" },
];

export default function Settings() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";

  const [minutes, setMinutes] = useState(15);
  const [options, setOptions] = useState([5, 10, 15, 30, 60]);
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);

  const [notify, setNotify] = useState(user?.source_notify || "all");
  const [savingNotify, setSavingNotify] = useState(false);

  useEffect(() => {
    if (user?.source_notify) setNotify(user.source_notify);
  }, [user]);

  useEffect(() => {
    if (!isAdmin) { setLoaded(true); return; }
    api.get("/settings/security").then((r) => {
      setMinutes(r.data.idle_timeout_minutes);
      if (r.data.options) setOptions(r.data.options);
      setLoaded(true);
    }).catch(() => setLoaded(true));
  }, [isAdmin]);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/settings/security", { idle_timeout_minutes: minutes });
      toast.success(`Auto sign-out set to ${minutes} minutes`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save");
    } finally { setSaving(false); }
  };

  const saveNotify = async (value) => {
    setNotify(value);
    setSavingNotify(true);
    try {
      await api.put("/auth/preferences", { source_notify: value });
      toast.success(value === "all"
        ? "You'll be emailed on every update to referrals you send"
        : "You'll be emailed only on the final outcome");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save");
    } finally { setSavingNotify(false); }
  };

  return (
    <div className="p-6 space-y-6 max-w-2xl" data-testid="settings-page">
      <div>
        <h1 className="font-head font-black text-3xl tracking-tight">Settings</h1>
        <p className="text-sm text-muted-foreground font-mono mt-1">Your preferences{isAdmin ? " · organization security" : ""}</p>
      </div>

      <div className="glass rounded-xl p-5" data-testid="source-notify-card">
        <div className="flex items-center gap-2 mb-1">
          <BellRing className="w-4 h-4 text-cyan-400" />
          <h2 className="font-head font-bold text-sm uppercase tracking-wide text-muted-foreground">Status updates on referrals you send</h2>
        </div>
        <p className="text-sm text-muted-foreground mt-1 mb-4">
          When the receiving line acts on a referral you logged, we email you automatically. Choose how often.
        </p>
        <div className="grid sm:grid-cols-2 gap-2">
          {NOTIFY_OPTIONS.map((o) => (
            <button
              key={o.value}
              onClick={() => saveNotify(o.value)}
              disabled={savingNotify}
              data-testid={`notify-option-${o.value}`}
              aria-pressed={notify === o.value}
              className={`text-left rounded-lg px-4 py-3 border transition-colors ${
                notify === o.value
                  ? "bg-cyan-500/12 border-cyan-500/40 text-foreground"
                  : "bg-slate-900/50 border-white/10 text-muted-foreground hover:text-foreground hover:border-white/25"
              }`}
            >
              <div className="text-sm font-semibold">{o.label}</div>
              <div className="text-xs text-muted-foreground mt-0.5">{o.hint}</div>
            </button>
          ))}
        </div>
      </div>

      {isAdmin && (
        <div className="glass rounded-xl p-5" data-testid="idle-setting-card">
          <div className="flex items-center gap-2 mb-1">
            <Clock className="w-4 h-4 text-cyan-400" />
            <h2 className="font-head font-bold text-sm uppercase tracking-wide text-muted-foreground">Automatic sign-out</h2>
          </div>
          <p className="text-sm text-muted-foreground mt-1 mb-4">
            Sign everyone out after a period of inactivity to protect patient data on unattended screens. Applies to all users.
          </p>

          {loaded ? (
            <div className="flex flex-wrap items-center gap-2">
              {options.map((m) => (
                <button
                  key={m}
                  onClick={() => setMinutes(m)}
                  data-testid={`idle-option-${m}`}
                  aria-pressed={minutes === m}
                  className={`rounded-lg px-4 py-2 text-sm font-semibold border transition-colors ${
                    minutes === m
                      ? "bg-cyan-500 text-slate-950 border-cyan-400"
                      : "bg-slate-900/50 text-muted-foreground border-white/10 hover:text-foreground hover:border-white/25"
                  }`}
                >
                  {m} min
                </button>
              ))}
              <Button onClick={save} disabled={saving} data-testid="save-idle-btn"
                      className="ml-auto bg-cyan-500 hover:bg-cyan-400 text-slate-950 font-semibold gap-2">
                <ShieldCheck className="w-4 h-4" /> {saving ? "Saving…" : "Save"}
              </Button>
            </div>
          ) : (
            <p className="text-xs text-muted-foreground font-mono">Loading…</p>
          )}
        </div>
      )}
    </div>
  );
}
